/**
 * tmm-api `/v1/reseller-admin/**` (TMMAPI-0078–0080, 0082). Each normalizer reads the vendored
 * contract's generated schema (`api/openapi.yaml`) and returns the console's own view, whose keys
 * are always present.
 *
 * Rules (H §6): the reseller always comes from the token — no reseller id is ever sent; any role
 * may GET, every other method needs OWNER or ADMIN (`403 RESELLER_ROLE_FORBIDDEN`).
 */
import { request } from './client';
import { ApiError, backendCode } from './errors';
import type {
  AgreementView as AgreementView_Gen,
  PayoutMethodView,
  PayoutStatementList,
  ResellerConsoleDashboard as ResellerConsoleDashboard_Gen,
  ResellerContractList as ResellerContractList_Gen,
  ResellerMeView as ResellerMeView_Gen,
  ResellerRevenueReport as ResellerRevenueReport_Gen,
  ResellerRevenueTenant as ResellerRevenueTenant_Gen,
  ResellerRevenueTotals as ResellerRevenueTotals_Gen,
  ResellerStandingView as ResellerStandingView_Gen,
  ResellerSubscriberCounts as ResellerSubscriberCounts_Gen,
  ResellerTenantList as ResellerTenantList_Gen,
  ResellerTenantView as ResellerTenantView_Gen,
  StripeConnectCreateRequest,
  StripeConnectOnboardingView,
  TenantPair as TenantPair_Gen,
} from './generated';
import { asResellerRole, type ResellerRole } from '@/auth/authContextValue';

/** `GET /v1/reseller-admin/me` (TMMAPI-0078). `role` is LIVE — it wins over the token's. */
export interface ResellerMeView {
  resellerId: string;
  /** `null` when the reseller has no name. */
  resellerName: string | null;
  role: ResellerRole;
  productSlug: string;
}

function normalizeMe(body: unknown): ResellerMeView {
  const raw = body as ResellerMeView_Gen;
  return {
    resellerId: String(raw.resellerId),
    resellerName: raw.resellerName,
    // An unknown role is the least privilege.
    role: asResellerRole(raw.role) ?? 'STAFF',
    productSlug: raw.productSlug,
  };
}

/**
 * A tenant assigned to this reseller (TMMAPI-0078, H §6.1). Every key is always present. `id` is
 * the saas-auth tenant id (for saas-auth's `/api/reseller-admin/tenants/{tenantId}/…`); the slug
 * names the tenant on every tmm-api call.
 */
export interface ResellerTenantView {
  id: number;
  slug: string;
  name: string;
  status: string;
  currency: string;
  aiEnabledDefault: boolean;
  /** The tenant's active add-ons (the tenant view's add-on list). */
  addonsActive: string[];
  activeSubscribers: number;
  createdAt: string;
  /**
   * `false` when the tenant cannot sell. `sellableReason` names the first failing cause. `null`
   * means sellable; the server still enforces it with its 409.
   */
  sellable: boolean;
  /** The first failing cause (TMMAPI-0095, 0097); `null` exactly when the tenant is sellable. */
  sellableReason: SellableReason | null;
  /** The tenant's assigned rate card (TMMAPI-0097), which may be closed or non-compliant; `null` = none. */
  rateCardId: number | null;
}

export type SellableReason = NonNullable<TenantPair_Gen['sellableReason']>;

const SELLABLE_REASONS: ReadonlySet<string> = new Set<SellableReason>([
  'NO_RESELLER',
  'NO_AGREEMENT',
  'NO_RATE_CARD',
  'CURRENCY_NOT_PRICED',
]);

function asSellableReason(value: unknown): SellableReason | null {
  return typeof value === 'string' && SELLABLE_REASONS.has(value)
    ? (value as SellableReason)
    : null;
}

/** `GET /tenants` — the array is `tenants` (not `content`), `page` is 0-based. */
export interface ResellerTenantPage {
  tenants: ResellerTenantView[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

function normalizeTenant(body: unknown): ResellerTenantView {
  const raw = body as ResellerTenantView_Gen;
  return {
    id: raw.id,
    slug: raw.slug,
    name: raw.name,
    status: raw.status,
    currency: raw.currency,
    aiEnabledDefault: raw.aiEnabledDefault,
    addonsActive: raw.addonsActive,
    activeSubscribers: raw.activeSubscribers,
    createdAt: raw.createdAt,
    sellable: raw.sellable,
    sellableReason: asSellableReason(raw.sellableReason),
    rateCardId: raw.rateCardId,
  };
}

function normalizeTenantPage(body: unknown): ResellerTenantPage {
  const raw = body as ResellerTenantList_Gen;
  return {
    tenants: raw.tenants.map(normalizeTenant),
    page: raw.page,
    size: raw.size,
    totalElements: raw.totalElements,
    totalPages: raw.totalPages,
  };
}

/* ---------------------------------------------------------------------------------------------
 * Reseller-wide (TMMAPI-0080, H §6.3 — binding). Periods are optional `from` / `to` ISO dates.
 * Money is `long` minor units (`…Minor`) and always sits beside its `currency`: totals are PER
 * CURRENCY and never added across currencies (see `src/components/PerCurrency.tsx`).
 * ------------------------------------------------------------------------------------------- */

/**
 * One agreement term set (the contract's reseller `AgreementView`). It carries no card since
 * TMMAPI-0154; `currency` is the minimum's.
 */
export interface ResellerAgreementView {
  payoutCadence: string;
  payoutMethod: string;
  minPayoutMinor: number;
  taxDocsStatus: string;
  monthlyMinimumMinor: number;
  probationMonths: number;
  currency: string;
  effectiveFrom: string;
  endedAt?: string;
  /** Official launch, always a 1st of the month; `null` until the product admin sets it. */
  officialLaunchOn: string | null;
}

/** `GET /agreement` — read-only; `current` is null when the reseller has no agreement yet. */
export interface ResellerAgreement {
  current: ResellerAgreementView | null;
  history: ResellerAgreementView[];
}

export type StandingState = 'GOOD' | 'PROBATION' | 'DEACTIVATION_DUE' | 'DEACTIVATED';

/** A charge to the reseller's card — a short month's shortfall, for one. */
export type ChargeStatus = 'PENDING' | 'REQUESTED' | 'SUCCEEDED' | 'FAILED' | 'REQUIRES_ACTION';

export const CHARGE_STATUSES: ReadonlySet<string> = new Set<ChargeStatus>([
  'PENDING',
  'REQUESTED',
  'SUCCEEDED',
  'FAILED',
  'REQUIRES_ACTION',
]);

/** A known charge status, else `null`. */
export function asChargeStatus(value: unknown): ChargeStatus | null {
  return typeof value === 'string' && CHARGE_STATUSES.has(value) ? (value as ChargeStatus) : null;
}

interface StandingMonth {
  month: string;
  currency: string;
  minimumMinor: number;
  earnedMinor: number;
  shortfallMinor: number;
  /** The card charge for this month's shortfall; `null` when there is no shortfall. */
  shortfallChargeId: number | null;
  chargeStatus: ChargeStatus | null;
}

/**
 * `GET /standing` — the minimum-commitment standing, in the agreement's currency.
 * Nothing is measured before `officialLaunchOn`, nor at all without it; a short month is charged
 * to the card and still counts toward probation.
 */
export interface ResellerStanding {
  /** `null` when tmm-api sends a state this console does not know — never shown as GOOD. */
  state: StandingState | null;
  outOfBand: ResellerStandingView_Gen['outOfBand'] | null;
  probationStartedOn?: string;
  probationEndsOn?: string;
  officialLaunchOn: string | null;
  /** The day probation is decided; `null` outside probation. */
  probationDeadline: string | null;
  /** The next standing email; `null` when none is due. */
  nextNoticeOn: string | null;
  /** Start of the open platform-managed window after deactivation (TMMAPI-0116), or null. */
  platformManagedSince: string | null;
  /** `null` when standing has no currency yet. */
  currency: string | null;
  months: StandingMonth[];
}

export interface RevenueTenantRow {
  slug: string;
  name: string;
  retailMinor: number;
  wholesaleMinor: number;
  /** What the customers paid less what we charged the reseller; may be negative. */
  resellerMarginMinor: number;
  /** On `/revenue`, "Your net": the margin less affiliate commission (no revenue share). */
  resellerShareMinor: number;
  affiliateCostMinor: number;
  taxMinor: number;
}

/** One currency's revenue totals. */
interface RevenueTotals extends Omit<RevenueTenantRow, 'slug' | 'name'> {
  currency: string;
}

/** `GET /revenue?from=&to=` — `retail` is gross collected. */
export interface ResellerRevenue {
  from: string;
  to: string;
  totals: RevenueTotals[];
}

interface SubscriberCounts {
  active: number;
  trialing: number;
  new: number;
  churned: number;
}

export interface DashboardMoney {
  currency: string;
  retailMinor: number;
  /** What the customers paid less what we charged; may be negative. */
  resellerMarginMinor: number;
  resellerShareMinor: number;
  payoutsPendingMinor: number;
  payoutsPaidMinor: number;
  affiliateCommissionMinor: number;
  /** The deficit and shortfall charges that succeeded in the window. */
  chargedToCardMinor: number;
}

/** `GET /dashboard?from=&to=` — counts cover the reseller's one tenant. */
export interface ResellerDashboard {
  from: string;
  to: string;
  subscribers: SubscriberCounts;
  guests: number;
  seats: number;
  connectedAccounts: number;
  money: DashboardMoney[];
  /** The reseller's one tenant (TMMAPI-0115), or `null` when none exists yet. */
  tenant: TenantPair_Gen | null;
}

export function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function normalizeAgreementView(body: unknown): ResellerAgreementView {
  const raw = body as AgreementView_Gen;
  const endedAt = asOptionalString(raw.endedAt);
  return {
    payoutCadence: raw.payoutCadence,
    payoutMethod: raw.payoutMethod,
    minPayoutMinor: raw.minPayoutMinor,
    taxDocsStatus: raw.taxDocsStatus,
    monthlyMinimumMinor: raw.monthlyMinimumMinor,
    probationMonths: raw.probationMonths,
    currency: raw.currency,
    effectiveFrom: raw.effectiveFrom,
    ...(endedAt !== undefined ? { endedAt } : {}),
    officialLaunchOn: asOptionalString(raw.officialLaunchOn) ?? null,
  };
}

function normalizeAgreement(body: unknown): ResellerAgreement {
  const raw = body as ResellerContractList_Gen;
  return {
    current: raw.current === null ? null : normalizeAgreementView(raw.current),
    history: raw.history.map(normalizeAgreementView),
  };
}

const STANDING_STATES: readonly StandingState[] = [
  'GOOD',
  'PROBATION',
  'DEACTIVATION_DUE',
  'DEACTIVATED',
];

function normalizeStanding(body: unknown): ResellerStanding {
  const raw = body as ResellerStandingView_Gen;
  const state = STANDING_STATES.find((s) => s === raw.state) ?? null;
  const startedOn = asOptionalString(raw.probationStartedOn);
  const endsOn = asOptionalString(raw.probationEndsOn);
  return {
    state,
    outOfBand: raw.outOfBand,
    ...(startedOn !== undefined ? { probationStartedOn: startedOn } : {}),
    ...(endsOn !== undefined ? { probationEndsOn: endsOn } : {}),
    officialLaunchOn: raw.officialLaunchOn,
    probationDeadline: raw.probationDeadline,
    nextNoticeOn: raw.nextNoticeOn,
    platformManagedSince: raw.platformManagedSince,
    currency: raw.currency,
    months: raw.months.map((month) => ({
      month: month.month,
      currency: month.currency,
      minimumMinor: month.minimumMinor,
      earnedMinor: month.earnedMinor,
      shortfallMinor: month.shortfallMinor,
      shortfallChargeId: month.shortfallChargeId,
      chargeStatus: asChargeStatus(month.chargeStatus),
    })),
  };
}

function normalizeRevenueAmounts(raw: ResellerRevenueTenant_Gen | ResellerRevenueTotals_Gen) {
  return {
    retailMinor: raw.retailMinor,
    wholesaleMinor: raw.wholesaleMinor,
    resellerMarginMinor: raw.resellerMarginMinor,
    resellerShareMinor: raw.resellerShareMinor,
    affiliateCostMinor: raw.affiliateCostMinor,
    taxMinor: raw.taxMinor,
  };
}

function normalizeRevenue(body: unknown): ResellerRevenue {
  const raw = body as ResellerRevenueReport_Gen;
  return {
    from: raw.from,
    to: raw.to,
    totals: raw.totals.map((total) => ({
      currency: total.currency,
      ...normalizeRevenueAmounts(total),
    })),
  };
}

function normalizeCounts(raw: ResellerSubscriberCounts_Gen): SubscriberCounts {
  return {
    active: raw.active,
    trialing: raw.trialing,
    new: raw.new,
    churned: raw.churned,
  };
}

function normalizeDashboardTenant(raw: TenantPair_Gen | null): TenantPair_Gen | null {
  if (raw === null) return null;
  return {
    slug: raw.slug,
    name: raw.name,
    status: raw.status,
    sellable: raw.sellable,
    sellableReason: asSellableReason(raw.sellableReason),
  };
}

function normalizeDashboard(body: unknown): ResellerDashboard {
  const raw = body as ResellerConsoleDashboard_Gen;
  return {
    from: raw.from,
    to: raw.to,
    subscribers: normalizeCounts(raw.subscribers),
    guests: raw.guests,
    seats: raw.seats,
    connectedAccounts: raw.connectedAccounts,
    money: raw.money.map((money) => ({
      currency: money.currency,
      retailMinor: money.retailMinor,
      resellerMarginMinor: money.resellerMarginMinor,
      resellerShareMinor: money.resellerShareMinor,
      payoutsPendingMinor: money.payoutsPendingMinor,
      payoutsPaidMinor: money.payoutsPaidMinor,
      affiliateCommissionMinor: money.affiliateCommissionMinor,
      chargedToCardMinor: money.chargedToCardMinor,
    })),
    tenant: normalizeDashboardTenant(raw.tenant),
  };
}

/** Drops blank values so an omitted filter is never sent as `?key=`. */
function queryOf(params: Record<string, string | undefined> | undefined): {
  query?: Record<string, string>;
} {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== '') query[key] = value;
  }
  return Object.keys(query).length > 0 ? { query } : {};
}

const RESELLER = '/v1/reseller-admin';

function tenantResource(slug: string): string {
  return `/v1/reseller-admin/tenants/${encodeURIComponent(slug)}`;
}

export const resellerAdminApi = {
  async me(): Promise<ResellerMeView> {
    return normalizeMe(await request<unknown>('GET', '/v1/reseller-admin/me'));
  },

  /** The reseller's assigned tenants, searched by `q`, server-paged (0-based `page`). */
  async listTenants(params?: {
    q?: string;
    page?: number;
    size?: number;
  }): Promise<ResellerTenantPage> {
    const query: Record<string, string | number> = {};
    const q = params?.q?.trim();
    if (q) query.q = q;
    if (params?.page !== undefined) query.page = params.page;
    if (params?.size !== undefined) query.size = params.size;
    return normalizeTenantPage(
      await request<unknown>(
        'GET',
        '/v1/reseller-admin/tenants',
        Object.keys(query).length > 0 ? { query } : {},
      ),
    );
  },

  async getTenant(slug: string): Promise<ResellerTenantView> {
    return normalizeTenant(await request<unknown>('GET', tenantResource(slug)));
  },

  /**
   * The reseller's one tenant (MM-D-107, TMMAPI-0115 `GET /v1/reseller-admin/tenant`);
   * `null` on `404 TENANT_NOT_FOUND` — before the tenant exists. Any other failure rethrows.
   */
  async getMyTenant(): Promise<ResellerTenantView | null> {
    try {
      return normalizeTenant(await request<unknown>('GET', '/v1/reseller-admin/tenant'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404 && backendCode(err) === 'TENANT_NOT_FOUND') {
        return null;
      }
      throw err;
    }
  },

  /** The tenant's AI default — the only tenant field a reseller may change (OWNER or ADMIN). */
  async patchTenant(
    slug: string,
    body: { aiEnabledDefault: boolean },
  ): Promise<ResellerTenantView> {
    return normalizeTenant(await request<unknown>('PATCH', tenantResource(slug), { json: body }));
  },

  async getAgreement(): Promise<ResellerAgreement> {
    return normalizeAgreement(await request<unknown>('GET', `${RESELLER}/agreement`));
  },

  async getStanding(): Promise<ResellerStanding> {
    return normalizeStanding(await request<unknown>('GET', `${RESELLER}/standing`));
  },

  async getRevenue(params?: {
    from?: string | undefined;
    to?: string | undefined;
  }): Promise<ResellerRevenue> {
    return normalizeRevenue(await request<unknown>('GET', `${RESELLER}/revenue`, queryOf(params)));
  },

  /**
   * The reseller's monthly settlements, newest first — one per currency per calendar month,
   * closed on day 2 of the next (the generated `PayoutStatementList`). No `status` parameter: the
   * screen filters the loaded list. Single-currency: every line shares the settlement's
   * `currency`.
   */
  async listSettlements(params?: {
    from?: string | undefined;
    to?: string | undefined;
    currency?: string | undefined;
  }): Promise<PayoutStatementList> {
    return request<PayoutStatementList>('GET', `${RESELLER}/settlements`, queryOf(params));
  },

  async getDashboard(params?: {
    from?: string | undefined;
    to?: string | undefined;
  }): Promise<ResellerDashboard> {
    return normalizeDashboard(
      await request<unknown>('GET', `${RESELLER}/dashboard`, queryOf(params)),
    );
  },

  /** `409 PAYOUT_METHOD_NOT_CONFIGURED` = no agreement yet. */
  async getPayoutMethod(): Promise<PayoutMethodView> {
    return request<PayoutMethodView>('GET', `${RESELLER}/payout-method`);
  },

  /** OWNER or ADMIN. Follow `onboardingUrl` only when it is `https:`. */
  async startStripeConnect(body: StripeConnectCreateRequest): Promise<StripeConnectOnboardingView> {
    return request<StripeConnectOnboardingView>(
      'POST',
      `${RESELLER}/payout-method/stripe-connect`,
      { json: body },
    );
  },

  /** OWNER or ADMIN — a fresh onboarding link for an unfinished Stripe Connect account. */
  async refreshStripeConnectLink(): Promise<StripeConnectOnboardingView> {
    return request<StripeConnectOnboardingView>(
      'POST',
      `${RESELLER}/payout-method/stripe-connect/onboarding-link`,
    );
  },
};
